import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const PricingCard = ({ 
  title,
  subtitle,
  features,
  featured = false 
}: { 
  title: string;
  subtitle: string;
  features: string[];
  featured?: boolean;
}) => (
  <motion.div 
    className={`p-12 ${featured ? 'bg-white text-black' : 'bg-[#0A0A0A] border border-[#41392e]'}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    <h3 className={`text-3xl font-serif mb-4 ${featured ? 'text-black' : 'text-[#c9ab81]'}`}>{title}</h3>
    <p className={`text-lg italic mb-8 ${featured ? 'text-gray-600' : 'text-gray-400'}`}>{subtitle}</p>
    <ul className="space-y-6 mb-12">
      {features.map((feature, index) => (
        <motion.li 
          key={index}
          className={`flex items-start space-x-4 ${featured ? 'text-gray-800' : 'text-gray-300'}`}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
        >
          <span className="text-[#c9ab81] mt-1">•</span>
          <span className="text-lg font-light">{feature}</span>
        </motion.li>
      ))}
    </ul>
    <a 
      href="https://docs.google.com/forms/d/e/1FAIpQLSc5mmPDqzUeexziQ_G5pmpiTNH2flL2p6DW5yWL2cu1lWC-6Q/viewform"
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-block group relative px-8 py-3 overflow-hidden ${
        featured 
          ? 'text-black hover:text-white' 
          : 'text-white hover:text-black'
      }`}
    >
      <div className={`absolute inset-0 w-0 ${
        featured 
          ? 'bg-black' 
          : 'bg-white'
      } transition-all duration-[750ms] ease-out group-hover:w-full`} />
      <span className="relative text-lg font-serif">
        En savoir plus
      </span>
      <div className={`absolute inset-0 border ${
        featured 
          ? 'border-black' 
          : 'border-white'
      } pointer-events-none`} />
    </a>
  </motion.div>
);

const Pricing = () => {
  const { t } = useTranslation();

  return (
    <div className="max-w-7xl mx-auto py-32 px-4">
      <motion.div 
        className="text-center mb-20"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="text-4xl font-serif mb-6">
          <span className="text-[#c9ab81] italic">{t('pricing.title')}</span>
        </h2>
        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
          {t('pricing.subtitle')}
        </p>
      </motion.div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        <PricingCard
          title={t('pricing.individual.title')}
          subtitle={t('pricing.individual.subtitle')}
          features={t('pricing.individual.features', { returnObjects: true }) as string[]}
        />
        <PricingCard
          title={t('pricing.duo.title')}
          subtitle={t('pricing.duo.subtitle')}
          features={t('pricing.duo.features', { returnObjects: true }) as string[]}
          featured
        />
      </div>
    </div>
  );
};

export default Pricing;